import { useQuery } from '@tanstack/react-query';

import http from '@/tscapi/service/httpservice';
import { Cookie } from '@/tscapi/utils/cookie';

export interface EasyPayScheduleItem {
    FullOrderNo: string;
    NextBillDate: string;
    NoOfEasyPay: string;
    InstallmentAmount: string;
    MaskedToken: string;
    CardExpiry: string;
    CardType: string;
    CardExpiresInDays: number;
}

export interface EasyPayScheduleResponse {
    Paging: {
        TotalRecords: number;
        TotalPages: number;
        CurrentPage: number;
    };
    InstallmentOrders: EasyPayScheduleItem[];
}

// Easy Pay Upcoming Payments
// accounts/{edp}/schedule/easypay

export const useUpcomingPayments = (isAuth: boolean, page = 1, pageSize = 10) => {
    const cookie: Cookie = new Cookie();
    const userAccessTokenEdp = cookie.UserAccessToken?.Edp;

    return useQuery<EasyPayScheduleResponse>({
        queryKey: ['easypayUpcomingPayments', userAccessTokenEdp, cookie.Language, page, pageSize],
        queryFn: () => fetchEasyPaySchedule(page, pageSize),
        enabled: isAuth && !!userAccessTokenEdp,
        useErrorBoundary: false,
        retry: false,
        refetchOnWindowFocus: false,
        keepPreviousData: true, // ✅ Keep previous data while fetching new page (v4 syntax)
    });

    async function fetchEasyPaySchedule(pageNo: number, pageSize: number): Promise<EasyPayScheduleResponse> {
        const base = '/api/v2/';
        if (
            !(
                cookie.UserAccessToken != undefined &&
                cookie.UserAccessToken.Token.length > 0 &&
                cookie.UserAccessToken.Edp > 0
            )
        ) {
            throw new Error('UserToken expired');
        }
        const lang = cookie.Language.substring(0, 2) || 'en';
        const edp = userAccessTokenEdp;
        const url = `${base}${lang}/accounts/${edp}/schedule/easypay?pageno=${pageNo}&pageSize=${pageSize}`;

        try {
            const response = await http.get(url);
            return response.data;
        } catch (error) {
            console.log('Error getting EasyPay schedule');
            throw new Error('Error getting EasyPay schedule');
        }
    }
};

// Easy Pay Payment History Detail
// /account/{edp}/orders/{orderNo}/installmentreview
export type InstallmentDetail = {
    Status: string;
    BillDate: string;
    Amount: string;
    AmountDue: string;
};

export type InstallmentReviewResponse = InstallmentDetail[];

export const usePaymentHistoryDetail = (orderNo: string | undefined) => {
    const cookie: Cookie = new Cookie();
    const lang = cookie.Language.substring(0, 2) || 'en';
    const userAccessTokenEdp = cookie.UserAccessToken?.Edp;

    return useQuery<InstallmentReviewResponse>({
        queryKey: ['easyPayDetail', userAccessTokenEdp, orderNo],
        queryFn: () => fetchOrderDetails(userAccessTokenEdp, lang, orderNo),
        enabled: !!orderNo,
    });

    async function fetchOrderDetails(
        userAccessTokenEdp: number | undefined,
        lang: string | undefined,
        orderNo: string | undefined,
    ): Promise<InstallmentReviewResponse> {
        const response = await http.get(
            `/api/v2/${lang}/accounts/${userAccessTokenEdp}/orders/${orderNo}/installmentreview`,
        );
        return response.data;
    }
};

// Easy Pay Balance History
// accounts/{edp}/schedule/easypay/monthly
export type MonthlyBalance = {
    DisplayBillMonth: string;
    BillMonth: string;
    Amount: string;
    AmountDue: number;
};

export type BalanceRemainingResponse = MonthlyBalance[];

export const useBalanceHistory = (enabled = true) => {
    const cookie: Cookie = new Cookie();
    const userAccessTokenEdp = cookie.UserAccessToken?.Edp;

    return useQuery<BalanceRemainingResponse>({
        queryKey: ['easyPayBalanceRemaining', userAccessTokenEdp, cookie.Language],
        queryFn: () => getBalanceRemaining(),
        enabled,
        useErrorBoundary: false,
        retry: false,
        refetchOnWindowFocus: false,
    });

    async function getBalanceRemaining(): Promise<BalanceRemainingResponse> {
        const base = '/api/v2/';
        if (
            !(
                cookie.UserAccessToken != undefined &&
                cookie.UserAccessToken.Token.length > 0 &&
                cookie.UserAccessToken.Edp > 0
            )
        ) {
            throw new Error('UserToken expired');
        }
        const lang = cookie.Language.substring(0, 2) || 'en';
        const edp = userAccessTokenEdp;
        const url = `${base}${lang}/accounts/${edp}/schedule/easypay/monthly`;

        try {
            const response = await http.get(url);
            return response.data;
        } catch (error) {
            console.log('Error getting EasyPay Monthly Balanche');
            throw new Error('Error getting EasyPay Monthly Balanch');
        }
    }
};

// Easy Pay Monthly Breakdown
// accounts/{edp}/schedule/easypay/{yearMonth}
export type MonthlyBreakdown = {
    FullOrderNo: 'string';
    NextBillDate: 'string';
    NoOfEasyPay: 'string';
    AmountDue: number;
    InstallmentAmount: 'string';
};

export type MonthlyBreakdownResponse = MonthlyBreakdown[];

export const useMonthlyBreakdown = (yearMonth?: string) => {
    const cookie: Cookie = new Cookie();
    const userAccessTokenEdp = cookie.UserAccessToken?.Edp;

    return useQuery<MonthlyBreakdownResponse>({
        queryKey: ['easyPayMonthlyBreakdown', yearMonth, userAccessTokenEdp, cookie.Language],
        queryFn: () => getMonthlyBreakdown(),
        useErrorBoundary: false,
        retry: false,
        refetchOnWindowFocus: false,
    });

    async function getMonthlyBreakdown(): Promise<MonthlyBreakdownResponse> {
        const base = '/api/v2/';
        if (
            !(
                cookie.UserAccessToken != undefined &&
                cookie.UserAccessToken.Token.length > 0 &&
                cookie.UserAccessToken.Edp > 0
            )
        ) {
            throw new Error('UserToken expired');
        }
        const lang = cookie.Language.substring(0, 2) || 'en';
        const edp = userAccessTokenEdp;
        const url = `${base}${lang}/accounts/${edp}/schedule/easypay/${yearMonth}`;

        try {
            const response = await http.get(url);
            return response.data;
        } catch (error) {
            console.log('Error getting EasyPay Monthly Balanche');
            throw new Error('Error getting EasyPay Monthly Balanch');
        }
    }
};
